import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useScenarioStore } from '../store/useScenarioStore';
import FleetEditor from '../components/FleetEditor';

interface SeedFromIndividual {
  scenarioName: string;
  vehicle: import('../types').VehicleInput | null;
  ownership_period_years: number;
}

export default function FleetEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentScenario,
    currentScenarioId,
    loadScenario,
    resetToNew,
    setAppMode,
    addLineItem,
    updateLineItemVehicle,
    updateLineItem,
    patchScenario,
    setFleetName,
  } = useScenarioStore();

  const isNew = !id || id === 'new';

  useEffect(() => {
    if (isNew) {
      const sameDraft =
        currentScenarioId === null && currentScenario.mode === 'fleet';
      if (!sameDraft) {
        resetToNew('fleet');
      } else {
        setAppMode('fleet');
      }

      // Phase 22.4: optional seed from "Create new in Fleet mode" affordance
      // on the individual editor. Copies the first vehicle as a single fleet
      // line item; quantities reset to 1; ownership_period preserved.
      const seed = (location.state as { seedFromIndividual?: SeedFromIndividual } | null)
        ?.seedFromIndividual;
      if (seed && seed.vehicle) {
        // Reset draft and seed from the passed vehicle. We do this on next tick
        // so the resetToNew() from above settles first.
        setTimeout(() => {
          const store = useScenarioStore.getState();
          if (store.currentScenarioId !== null) return;
          const baseName = (seed.scenarioName || 'New Fleet').trim();
          patchScenario({ name: baseName });
          setFleetName(baseName);
          // The default reset already inserts one default line item; replace
          // its vehicle with the copied one rather than appending.
          const lis = store.currentScenario.line_items ?? [];
          if (lis.length > 0 && seed.vehicle) {
            updateLineItemVehicle(0, { ...seed.vehicle, vehicle_id: crypto.randomUUID() });
            updateLineItem(0, { quantity: 1 });
          } else if (seed.vehicle) {
            addLineItem();
            updateLineItemVehicle(0, { ...seed.vehicle, vehicle_id: crypto.randomUUID() });
          }
          // Clear the navigation state so a refresh doesn't reseed.
          navigate(location.pathname, { replace: true });
        }, 0);
      }
    } else {
      const idMatch =
        currentScenarioId !== null &&
        String(currentScenarioId) === String(id);
      if (!idMatch) {
        loadScenario(id as string);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cross-mode redirect: this page is fleet-only.
  useEffect(() => {
    if (
      !isNew &&
      currentScenarioId !== null &&
      String(currentScenarioId) === String(id) &&
      currentScenario.mode === 'individual'
    ) {
      navigate(`/scenario/individual/${currentScenarioId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScenarioId, currentScenario.mode]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Mode badge */}
      <div className="mb-6 flex items-center gap-2">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest"
          style={{
            background: 'var(--red-dim)',
            border: '1px solid var(--red-bright)',
            color: 'var(--white)',
            fontFamily: 'Barlow Condensed, sans-serif',
          }}
        >
          Fleet mode
        </span>
        {!isNew && (
          <button
            onClick={() => {
              if (
                window.confirm(
                  'Create a new Individual scenario? The first line item\'s vehicle will be copied; quantities and other line items will be dropped.',
                )
              ) {
                const lis = currentScenario.line_items ?? [];
                const firstVehicle = lis[0]?.vehicle ?? null;
                navigate('/scenario/individual/new', {
                  state: {
                    seedFromFleet: {
                      scenarioName: currentScenario.name,
                      vehicle: firstVehicle,
                    },
                  },
                });
              }
            }}
            className="text-xs underline underline-offset-2"
            style={{ color: 'var(--white-dim)', fontFamily: 'Barlow, sans-serif' }}
          >
            Create new in Individual mode
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        All Scenarios
      </button>

      <FleetEditor />
    </div>
  );
}
